<template>
    <div class="import-events-modal-wrap">
    <NoButtonModal :open="open" @close="$emit('close')">
        <div class="import-modal">
            <div class="import-modal__header">
                <h2>Import Events from CSV</h2>
                <button class="close-corner-x" type="button" @click="$emit('close')">
                    <span class="material-icons-outlined dark-text">close</span>
                </button>
            </div>

            <!-- Step 1: File selection -->
            <div v-if="step === 'select'" class="import-modal__body">
                <div class="import-hint">
                    <p>Upload a <strong>.csv</strong> file with the following columns:</p>
                    <code>name, location, event_date, description, dashboard_id, member_dashboard_id</code>
                    <p class="mt-2 text-muted">
                        <strong>Required:</strong> name, event_date, dashboard_id, member_dashboard_id<br />
                        <strong>Optional:</strong> location, description
                    </p>
                </div>

                <button class="button secondary mb-3" @click="downloadSample">
                    <span class="material-icons-outlined" style="font-size:16px; vertical-align:middle">download</span>
                    Download Sample CSV
                </button>

                <div
                    class="drop-zone"
                    :class="{ 'drop-zone--active': isDragging }"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="onDrop"
                    @click="$refs.fileInput.click()"
                >
                    <span class="material-icons-outlined" style="font-size:40px; color:#5057c3">upload_file</span>
                    <p>Drag &amp; drop a CSV file here, or <strong>click to browse</strong></p>
                    <p class="text-muted" style="font-size:0.8rem">Supports .csv files from Excel (Windows/Mac)</p>
                </div>

                <input
                    ref="fileInput"
                    type="file"
                    accept=".csv,text/csv,application/vnd.ms-excel"
                    style="display:none"
                    @change="onFileSelected"
                />

                <!-- Validating spinner -->
                <div v-if="isValidating" class="d-flex align-items-center mt-3" style="gap:8px">
                    <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                    <span style="font-size:0.9rem">Validating CSV with server...</span>
                </div>

                <p v-if="fileError" class="text-danger mt-2">{{ fileError }}</p>
            </div>

            <!-- Step 2: Preview -->
            <div v-else-if="step === 'preview'" class="import-modal__body">
                <div class="preview-summary">
                    <span class="badge-valid">{{ validCount }} valid row(s)</span>
                    <span class="badge-invalid ml-2" v-if="invalidCount">{{ invalidCount }} row(s) with errors</span>
                    <span class="file-name ml-2">{{ fileName }}</span>
                </div>

                <div class="preview-table-wrap">
                    <table class="preview-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Event Name</th>
                                <th>Location</th>
                                <th>Event Date</th>
                                <th>Description</th>
                                <th>SC Dashboard ID</th>
                                <th>Member Dashboard ID</th>
                                <th>Event Status</th>
                                <th>Import Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in previewRows"
                                :key="row.row"
                                :class="row.valid ? 'row-valid' : 'row-invalid'"
                            >
                                <td>{{ row.row }}</td>
                                <td>{{ row.data.name || '—' }}</td>
                                <td>{{ row.data.location || '—' }}</td>
                                <td>{{ row.data.event_date || '—' }}</td>
                                <td>{{ row.data.description || '—' }}</td>
                                <td>{{ row.data.dashboard_id || '—' }}</td>
                                <td>{{ row.data.member_dashboard_id || '—' }}</td>
                                <td><span class="badge-inactive">Inactive</span></td>
                                <td>
                                    <div v-if="!row.valid">
                                        <span class="badge-invalid">Error</span>
                                        <ul class="error-messages">
                                            <li v-for="(err, i) in row.errors" :key="i">{{ err }}</li>
                                        </ul>
                                    </div>
                                    <span v-else class="badge-valid">Ready</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Error summary below table -->
                <div v-if="invalidCount" class="error-list mt-3">
                    <strong>Errors found:</strong>
                    <ul>
                        <li v-for="row in previewRows.filter(r => !r.valid)" :key="row.row">
                            Row {{ row.row }}: {{ row.errors.join(', ') }}
                        </li>
                    </ul>
                </div>

                <p v-if="!validCount" class="text-danger mt-2">
                    No valid rows to import. Please fix the CSV and try again.
                </p>
            </div>

            <!-- Actions -->
            <div class="import-modal__footer">
                <template v-if="step === 'select'">
                    <button class="button secondary" @click="$emit('close')">Cancel</button>
                </template>
                <template v-else-if="step === 'preview'">
                    <button class="button secondary" @click="reset">
                        <span class="material-icons-outlined" style="font-size:16px; vertical-align:middle">arrow_back</span>
                        Choose Another File
                    </button>
                    <button
                        class="button primary ml-2"
                        :disabled="!validCount || isImporting"
                        @click="importEvents"
                    >
                        {{ isImporting ? 'Importing...' : `Import ${validCount} Event${validCount !== 1 ? 's' : ''}` }}
                    </button>
                </template>
            </div>
        </div>
    </NoButtonModal>
    </div>
</template>

<script>
    import NoButtonModal from '@/components/general/modals/NoButtonModal';
    import { streetTeam } from '@/util/apiRequests';

    export default {
        name: 'ImportEventsModal',
        components: { NoButtonModal },
        props: {
            open: { type: Boolean, required: true },
        },
        data() {
            return {
                step: 'select',
                fileName: '',
                selectedFile: null,
                previewRows: [],
                validCount: 0,
                invalidCount: 0,
                fileError: '',
                isDragging: false,
                isValidating: false,
                isImporting: false,
            };
        },
        methods: {
            downloadSample() {
                const csv =
                    'name,location,event_date,description,dashboard_id,member_dashboard_id\r\n' +
                    'Downtown Health Fair,City Hall Plaza,2026-05-10,Annual community health event,1,2\r\n' +
                    'Senior Wellness Walk,Riverside Park,2026-05-17,,2,3\r\n' +
                    'School Outreach Program,Lincoln Elementary,2026-05-24,Pediatric health screenings,3,4\r\n';

                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'street_team_events_sample.csv';
                a.click();
                URL.revokeObjectURL(url);
            },
            onDrop(e) {
                this.isDragging = false;
                const file = e.dataTransfer.files[0];
                if (file) this.processFile(file);
            },
            onFileSelected(e) {
                const file = e.target.files[0];
                if (file) this.processFile(file);
                e.target.value = '';
            },
            async processFile(file) {
                this.fileError = '';
                const ext = file.name.split('.').pop().toLowerCase();
                if (ext !== 'csv') {
                    this.fileError = 'Only .csv files are supported.';
                    return;
                }
                this.fileName = file.name;
                this.selectedFile = file;

                // Call backend validate-csv to get per-row preview data
                this.isValidating = true;
                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    const res = await this.$api.post(streetTeam.validateCsv(), formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });

                    const responseData = res.data?.data ?? res.data;
                    const { rows = [], valid = 0, invalid = 0 } = responseData;

                    if (rows.length) {
                        this.previewRows = rows;
                        this.validCount = valid;
                        this.invalidCount = invalid;
                        this.step = 'preview';
                    } else {
                        this.fileError = res.data?.message || 'Validation failed. Please check the file and try again.';
                    }
                } catch (err) {
                    this.fileError = 'Failed to validate the CSV. Please try again.';
                } finally {
                    this.isValidating = false;
                }
            },
            async importEvents() {
                this.isImporting = true;
                try {
                    const formData = new FormData();
                    formData.append('file', this.selectedFile);

                    const res = await this.$api.post(streetTeam.importCsv(), formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });

                    if (res.status >= 400) {
                        this.$toasted.error(res.data?.message || 'Import failed.');
                        return;
                    }

                    const { created = 0, failed = 0, errors = [] } = res.data.data;
                    if (failed > 0) {
                        errors.forEach(e => this.$toasted.error(`Row ${e.row}: ${e.error}`));
                    }
                    if (created > 0) {
                        this.$toasted.success(`${created} event${created !== 1 ? 's' : ''} imported successfully!`);
                    }
                    this.$emit('imported', created);
                } catch (err) {
                    this.$toasted.error('Import failed. Please try again.');
                } finally {
                    this.isImporting = false;
                }
            },
            reset() {
                this.step = 'select';
                this.fileName = '';
                this.selectedFile = null;
                this.previewRows = [];
                this.validCount = 0;
                this.invalidCount = 0;
                this.fileError = '';
                this.isValidating = false;
                this.isImporting = false;
            },
        },
        watch: {
            open(val) {
                if (!val) this.reset();
            },
        },
    };
</script>

<style scoped>
    .import-modal {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .import-modal__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
    }

    .import-modal__header h2 {
        font-size: 1.2rem;
        font-weight: 700;
        margin: 0;
    }

    .import-modal__body {
        flex: 1;
    }

    .import-modal__footer {
        margin-top: 1.5rem;
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
    }

    .import-hint {
        background: #f1f7ff;
        border-radius: 8px;
        padding: 12px 16px;
        margin-bottom: 1rem;
        font-size: 0.9rem;
    }

    .import-hint code {
        background: #dce8ff;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 0.85rem;
    }

    .drop-zone {
        border: 2px dashed #5057c3;
        border-radius: 12px;
        padding: 32px 20px;
        text-align: center;
        cursor: pointer;
        transition: background 0.2s;
        margin-bottom: 0.5rem;
    }

    .drop-zone--active,
    .drop-zone:hover {
        background: #f0f1ff;
    }

    .drop-zone p {
        margin: 4px 0;
        font-size: 0.95rem;
    }

    .preview-summary {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        font-size: 0.9rem;
    }

    .file-name {
        color: #666;
        font-style: italic;
    }

    .preview-table-wrap {
        max-height: 320px;
        overflow-x: auto;
        overflow-y: auto;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        -webkit-overflow-scrolling: touch;
    }

    .preview-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
    }

    .preview-table th {
        background: #f1f7ff;
        padding: 8px 10px;
        text-align: left;
        position: sticky;
        top: 0;
        z-index: 1;
        border-bottom: 1px solid #dee2e6;
        white-space: nowrap;
    }

    .preview-table td {
        padding: 7px 10px;
        border-bottom: 1px solid #f0f0f0;
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .row-valid td {
        background: #f6fff8;
    }

    .row-invalid td {
        background: #fff5f5;
    }

    .badge-inactive {
        background: #e2e3e5;
        color: #383d41;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 0.8rem;
        white-space: nowrap;
    }

    .badge-valid {
        background: #d4edda;
        color: #155724;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 0.8rem;
        white-space: nowrap;
    }

    .badge-invalid {
        background: #f8d7da;
        color: #721c24;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 0.8rem;
        white-space: nowrap;
    }

    .error-list {
        font-size: 0.85rem;
        background: #fff5f5;
        border-radius: 8px;
        padding: 10px 14px;
    }

    .error-list ul {
        margin: 4px 0 0 0;
        padding-left: 1.2rem;
    }

    .error-messages {
        margin: 4px 0 0 0;
        padding-left: 1rem;
        font-size: 0.78rem;
        color: #721c24;
    }

    .error-messages li {
        margin-bottom: 2px;
    }

    .ml-2 {
        margin-left: 0.5rem;
    }

    .mb-3 {
        margin-bottom: 1rem;
    }

    .mt-2 {
        margin-top: 0.5rem;
    }

    .mt-3 {
        margin-top: 0.75rem;
    }

    .text-muted {
        color: #6c757d;
    }
</style>

<!-- Non-scoped: override global modal__content max-width for this dialog -->
<style>
    .import-events-modal-wrap .modal__content {
        max-width: min(860px, calc(100vw - 40px));
        width: calc(100vw - 40px);
    }

    @media (max-width: 600px) {
        .import-events-modal-wrap .modal__content {
            width: calc(100vw - 20px);
            padding: 14px;
        }

        .import-events-modal-wrap .import-modal__footer {
            flex-direction: column;
        }

        .import-events-modal-wrap .import-modal__footer button {
            width: 100%;
            margin-left: 0 !important;
        }

        .import-events-modal-wrap .preview-summary {
            flex-wrap: wrap;
            gap: 4px;
        }
    }
</style>
